import { Portal } from '@mantine/core';
import {
  BenefitDocumentDto,
  BenefitDocumentType,
  PlanMarket,
} from '@zorro/clients';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Loader } from '@zorro/design-system/v2/Loader';
import { formatDateISO } from '@zorro/shared/formatters';
import { Center, Stack } from '@zorro/web/design-system';
import {
  PaymentStatusSection,
  toUpdateBenefitPaymentStatusDto,
  useAnalytics,
} from '@zorro/web/shared-ui';
import {
  createBenefitDocument,
  showErrorNotification,
  useFlowId,
  useIdempotencyKey,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import { FormFooter } from '../../FormFooter';
import { EnrollmentConfirmationStep } from '../EnrollmentConfirmationModal';
import { EnrollmentCommunicationSection } from './sections/EnrollmentCommunicationSection';
import { EnrollmentCoveragePeriodSection } from './sections/EnrollmentCoveragePeriodSection';
import { EnrollmentEvidenceSection } from './sections/EnrollmentEvidenceSection';
import { EnrollmentPaymentDetailsSection } from './sections/EnrollmentPaymentDetailsSection';
import { EnrollmentPolicyDetailsSection } from './sections/EnrollmentPolicyDetailsSection';
import { EnrollmentPremiumAmountsSection } from './sections/EnrollmentPremiumAmountsSection';
import { useEnrollmentConfirmationFlowForm } from './useEnrollmentConfirmationFlowForm';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
  onClose: () => void;
};

export const EnrollmentConfirmation = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  benefitId,
  onClose,
}: Props) => {
  const idempotencyKey = useIdempotencyKey();
  const flowId = useFlowId();
  const { sendEvent } = useAnalytics();
  const { startLoading, stopLoading } = useLoadingOverlay();

  const { tryMutate: tryMutateConfirmEnrollment } = useMonolithMutation({
    method: 'majorMedicalControllerConfirmEnrollment',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
  });
  const { mutate: mutateBenefitDocument } = useMonolithMutation({
    method: 'benefitDocumentsControllerCreateBenefitDocument',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
    shouldInvalidateQueries: false,
  });
  const { mutate: mutateBenefitDocumentRemove } = useMonolithMutation({
    method: 'benefitDocumentsControllerRemoveBenefitDocument',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
    shouldInvalidateQueries: false,
  });

  const { data: onboardingPeriod, isLoading: isLoadingOnboardingPeriod } =
    useMonolithQuery({
      method: 'onboardingPeriodsControllerFindOne',
      params: [employeeId, onboardingPeriodId],
    });
  const { data: benefits, isLoading: isLoadingBenefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const benefit = benefits?.allBenefits.find(({ id }) => id === benefitId);
  const benefitStatus = benefit?.status;

  const {
    enrollmentConfirmationForm,
    paymentStatusForm,
    validatePaymentStatus,
    isLoadingPaymentStatus,
    coveragePeriodForm,
    requiredActions,
    isSubmitEnabled,
    shouldHideCommunication,
  } = useEnrollmentConfirmationFlowForm({
    employerId,
    employeeId,
    onboardingPeriodId,
    benefitId,
  });

  const allBenefitDocuments = onboardingPeriod?.benefitDocuments ?? [];

  const handleEvidenceUpload = async (
    confirmationEvidence: (File | BenefitDocumentDto)[] | undefined
  ) => {
    if (!confirmationEvidence) return;

    const filesToUpload = confirmationEvidence.filter(
      (item): item is File => item instanceof File
    );
    const newDocuments = await Promise.all(
      filesToUpload.map((file) =>
        createBenefitDocument(
          employeeId,
          employerId,
          file,
          onboardingPeriodId,
          true,
          BenefitDocumentType.CONFIRMATION
        )
      )
    );

    const keptIds = confirmationEvidence
      .filter((item): item is BenefitDocumentDto => !(item instanceof File))
      .map((doc) => doc.id);
    const previousIds = allBenefitDocuments
      .filter(({ type }) => type === BenefitDocumentType.CONFIRMATION)
      .map(({ id }) => id);
    const idsToRemove = previousIds.filter((id) => !keptIds.includes(id));

    await Promise.all(
      idsToRemove.map((id) => mutateBenefitDocumentRemove([id], { flowId }))
    );
    await Promise.all(
      newDocuments.map((doc) => mutateBenefitDocument([doc], { flowId }))
    );
  };

  const handleSubmit = async () => {
    startLoading();
    await sendEvent('click_confirm_enrollment', {
      onboarding_period_id: onboardingPeriodId,
    });

    const paymentStatus = await validatePaymentStatus();
    const isValidEnrollmentConfirmation =
      await enrollmentConfirmationForm.trigger();
    const isValidCoveragePeriod = await coveragePeriodForm.trigger();

    if (
      !paymentStatus ||
      !isValidEnrollmentConfirmation ||
      !isValidCoveragePeriod ||
      !benefit?.plan
    ) {
      stopLoading();
      return;
    }

    const {
      confirmationEvidence,
      planMarket: planMarketFormValue,
      memberId,
      shouldSendEnrollmentConfirmationEmail,
      premium: premiumFormValue,
    } = enrollmentConfirmationForm.getValues();
    const { effectiveFrom, effectiveUntil } = coveragePeriodForm.getValues();

    if (!effectiveFrom || !effectiveUntil) {
      stopLoading();
      return;
    }

    const planMarket =
      planMarketFormValue === 'NOT_APPLICABLE'
        ? null
        : (planMarketFormValue as PlanMarket | null);

    await handleEvidenceUpload(confirmationEvidence);

    const confirmEnrollmentResult = await tryMutateConfirmEnrollment(
      [
        benefit.id,
        idempotencyKey.get(),
        {
          effectiveFrom: formatDateISO(effectiveFrom),
          effectiveUntil: formatDateISO(effectiveUntil),
          memberId: memberId ? memberId : null,
          planMarket,
          premium: premiumFormValue,
          paymentStatus: toUpdateBenefitPaymentStatusDto(paymentStatus),
          shouldSendConfirmationEmail: shouldSendEnrollmentConfirmationEmail,
        },
      ],
      { flowId }
    );
    if (!confirmEnrollmentResult.isOk()) {
      stopLoading();
      return;
    }

    if (confirmEnrollmentResult.value.failedToSendConfirmationEmail) {
      showErrorNotification({
        title: 'Confirmation email not sent',
        message:
          'The enrollment was confirmed, but the confirmation email could not be sent',
      });
    }

    stopLoading();
    onClose();
  };

  const isLoading = Boolean(isLoadingOnboardingPeriod || isLoadingBenefits);

  if (isLoading) {
    return (
      <Center mih="calc(100vh - 18.5rem)">
        <Loader />
      </Center>
    );
  }

  let sectionNumber = 1;

  return (
    <EnrollmentConfirmationStep
      employerId={employerId}
      employeeId={employeeId}
      onboardingPeriodId={onboardingPeriodId}
      benefitId={benefitId}
    >
      <Stack>
        <EnrollmentPaymentDetailsSection
          sectionNumber={sectionNumber++}
          employeeId={employeeId}
          employerId={employerId}
          onboardingPeriodId={onboardingPeriodId}
        />
        <Divider />
        <PaymentStatusSection
          sectionNumber={sectionNumber++}
          title="Update the payment status"
          onboardingPeriodId={onboardingPeriodId}
          paymentStatusForm={paymentStatusForm}
          shouldShowCompleted
          isDisabled={isLoadingPaymentStatus}
        />
        <Divider />
        <EnrollmentPremiumAmountsSection
          sectionNumber={sectionNumber++}
          onboardingPeriodId={onboardingPeriodId}
          enrollmentConfirmationForm={enrollmentConfirmationForm}
        />
        <Divider />
        <EnrollmentCoveragePeriodSection
          sectionNumber={sectionNumber++}
          employeeId={employeeId}
          onboardingPeriodId={onboardingPeriodId}
          coveragePeriodForm={coveragePeriodForm}
        />
        <Divider />
        <EnrollmentPolicyDetailsSection
          sectionNumber={sectionNumber++}
          onboardingPeriodId={onboardingPeriodId}
          enrollmentConfirmationForm={enrollmentConfirmationForm}
        />
        <Divider />
        <EnrollmentEvidenceSection
          sectionNumber={sectionNumber++}
          onboardingPeriodId={onboardingPeriodId}
          enrollmentConfirmationForm={enrollmentConfirmationForm}
        />
        {/* The server holds the email while the plan awaits payment. */}
        {requiredActions.length > 0 && !shouldHideCommunication && (
          <EnrollmentCommunicationSection
            sectionNumber={sectionNumber++}
            onboardingPeriodId={onboardingPeriodId}
            requiredActions={requiredActions}
            benefitStatus={benefitStatus}
            enrollmentConfirmationForm={enrollmentConfirmationForm}
          />
        )}
      </Stack>
      <Portal target="#enrollment-confirmation-modal-footer">
        <FormFooter
          primaryLabel="Confirm enrollment"
          primaryButtonProps={{
            onClick: handleSubmit,
            disabled: !isSubmitEnabled,
          }}
          paperProps={{ pos: 'sticky', bottom: '0' }}
        />
      </Portal>
    </EnrollmentConfirmationStep>
  );
};
